import CameraIcon from '@assets/icons/camera.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import { createStyles } from '@/contexts/theme-context';
import { ScannerType } from './scanner-types';
import { shadow, TYPOGRAPHY } from '@/styles';
import { Text, TouchableOpacity, View } from 'react-native';

const SCANNER_BUTTON_BORDER_RADIUS = 48;

interface Props {
  changeScannerType: () => void;
  scannerType: ScannerType;
}

export const ScannerSwitch: React.FC<Props> = ({
  changeScannerType,
  scannerType,
}) => {
  const [styles, theme] = useStyles();

  const isExternalScanner = scannerType === ScannerType.External;
  const isCameraScanner = scannerType === ScannerType.Camera;
  const scanIconStroke = isExternalScanner
    ? theme.sk.color.icon.secondary
    : theme.sk.color.icon.ondark;

  return (
    <View style={[styles.base, isCameraScanner ? styles.darkContainer : null]}>
      <View style={styles.content}>
        <TouchableOpacity
          disabled={isExternalScanner}
          onPress={changeScannerType}
          style={[
            styles.scannerButton,
            isExternalScanner ? styles.active : null,
          ]}
          testID="scanner-switch-scan"
        >
          <ScanIcon height={16} stroke={scanIconStroke} width={16} />
          {isExternalScanner ? (
            <Text style={[TYPOGRAPHY.pSemiBold, styles.scannerText]}>
              {i18n.t('scan')}
            </Text>
          ) : null}
        </TouchableOpacity>
        <TouchableOpacity
          disabled={isCameraScanner}
          onPress={changeScannerType}
          style={[styles.scannerButton, isCameraScanner ? styles.active : null]}
          testID="scanner-switch-camera"
        >
          <CameraIcon
            height={16}
            stroke={theme.sk.color.icon.secondary}
            width={16}
          />
          {isCameraScanner ? (
            <Text style={[TYPOGRAPHY.pSemiBold, styles.scannerText]}>
              {i18n.t('camera')}
            </Text>
          ) : null}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  active: {
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.2,
      radius: SCANNER_BUTTON_BORDER_RADIUS,
      x: 0,
      y: 4,
    }),
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: SCANNER_BUTTON_BORDER_RADIUS,
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 12,
  },

  base: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: SCANNER_BUTTON_BORDER_RADIUS,
    overflow: 'hidden',
    padding: 4,
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  darkContainer: {
    backgroundColor: theme.sk.color.bg.dark.default,
  },
  scannerButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  scannerText: {
    fontSize: 14,
  },
}));
